/// <amd-module name="VSS/Features/Monaco/Components/EditorBase" />

import * as React from "react";
import { ObservableLike } from "VSS/Core/Observable";
import { IBaseEditorProps, IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { IMonacoEnvironmentService } from "VSS/Features/Monaco/MonacoEnvironment";
import * as Resources from "VSS/Features/Monaco/Resources";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssThemeService } from "VSS/Platform/Theming";
import { combineUrlPaths } from "VSS/Platform/Util/Url";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { css } from "VSSUI/Util";

const editorDefaultOptions: monaco.editor.IEditorOptions = {
    fontSize: 12,
    lineNumbersMinChars: 5
};

interface IDisposableWidget extends monaco.IDisposable {
    id: string;
}

export abstract class EditorBase<P extends IBaseEditorProps> extends VssComponent<P, {}> {
    private editor?: monaco.editor.IEditor;
    private containerRef = React.createRef<HTMLDivElement>();
    private actionDisposables: monaco.IDisposable[] = [];
    private widgetDisposables: IDisposableWidget[] = [];
    private themeListenerAdded: boolean;
    private isContentUserEdited: boolean = false;
    private isContentChangeFromProps: boolean = false;
    private throttledContentChangedTimeout: number | null = null;

    public constructor(props: P, context: IVssComponentContext) {
        super(props, context);

        const monacoEnvService = this.context.pageContext.getService<IMonacoEnvironmentService>("IMonacoEnvironmentService");
        monacoEnvService.setupMonacoEnvironment(this.props.fileExtension, this.props.fileUrl, this.actionDisposables, this.props.delayedHighlighting);
    }

    public render(): JSX.Element {
        return <div ref={this.containerRef} className={css("vss-base-editor", this.props.className)} />;
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.containerRef.current) {
            const editorConstructionOptions: monaco.editor.IEditorConstructionOptions = this.getEditorOptions();
            this.editor = this.createEditor(this.containerRef.current, editorConstructionOptions);

            // Add editor actions
            const actions: monaco.editor.IActionDescriptor[] =
                typeof this.props.actions === "function" ? this.props.actions(this.editor) || [] : this.props.actions || [];

            if (this.props.elementToFocusOnEscape) {
                // Add Esc key handler to editor(s) to lose focus if no known widgets are visible
                actions.push({
                    id: "vssf-escape",
                    label: Resources.EscapeToLoseFocus,
                    keybindings: [monaco.KeyCode.Escape],
                    precondition: "!suggestWidgetVisible && !markersNavigationVisible && !parameterHintsVisible && !findWidgetVisible",
                    run: (e: monaco.editor.ICodeEditor) => {
                        const monacoElement = document.activeElement as HTMLElement;
                        const elementToFocus = this.props.elementToFocusOnEscape
                            ? (document.querySelector(this.props.elementToFocusOnEscape) as HTMLElement)
                            : null;

                        // Attempt to focus the desired element if it's valid to do so.
                        if (
                            elementToFocus &&
                            elementToFocus.focus &&
                            !elementToFocus.hasAttribute("disabled") &&
                            !elementToFocus.classList.contains("disabled") &&
                            elementToFocus.style.visibility !== "hidden" &&
                            elementToFocus.style.display !== "none"
                        ) {
                            elementToFocus.focus();
                        }

                        // If we can't focus the desired element, then at least get the focus out of Monaco.
                        if (document.activeElement === monacoElement && monacoElement && monacoElement.blur) {
                            monacoElement.blur();
                        }
                    }
                });
            }
            this.addEditorActions(actions);

            this.isContentChangeFromProps = true;
            this.setEditorContent(this.editor);
            this.sendThrottledContentChangedEvent();
            this.isContentChangeFromProps = false;

            // Widgets need the editor content to be set in order to display properly
           const widgets = this.props.widgets;
           if (widgets) {
               this.addEditorWidgets(this.editor, ObservableLike.isObservable(widgets) ? widgets.value : widgets);
               ObservableLike.subscribe(widgets, this.onWidgetsChange);
           }

            if (this.props.editorRef && typeof this.props.editorRef === "function") {
                this.props.editorRef(this.editor);
            }
        }

        window.addEventListener("resize", this.onResize);
    }

    public componentDidUpdate(prevProps: P, state: {}) {
        super.componentDidUpdate(prevProps, state);

        if (!this.editor) {
            return;
        }

        // Update the content. Content changes may originate externally or internally from user editing.
        if (!this.props.lazyVisualContentChange) {
            this.isContentChangeFromProps = true;
            this.updateEditorContent(this.editor);
            this.isContentChangeFromProps = false;
        }

        // Update the Monaco theme
        if (this.props.monacoTheme && this.props.monacoTheme !== prevProps.monacoTheme) {
            const themeName = this.getEditorTheme();
            monaco.editor.setTheme(themeName);
        }

        if (isFeatureFlagEnabled(this.context.pageContext, "WebAccess.PullRequest.MultipleRequests", false)) {
            // If the instance of the widgets observable has changed, unsubscribe to the old one and subscribe to the new one.
            if (prevProps.widgets !== this.props.widgets) {
                if (this.props.widgets) {
                    this.widgetDisposables.forEach(d => d.dispose());
                    this.widgetDisposables = [];
                    if (prevProps.widgets) {
                        ObservableLike.unsubscribe(prevProps.widgets, this.onWidgetsChange);
                    }

                    this.addEditorWidgets(this.editor, ObservableLike.isObservable(this.props.widgets) ? this.props.widgets.value : this.props.widgets);
                    ObservableLike.subscribe(this.props.widgets, this.onWidgetsChange);
                }
            }
        }
        

        // Re-layout, in case the size of the viewport changed.
        this.editor.layout();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        if (this.containerRef.current) {
            this.containerRef.current.innerHTML = "";
        }

        this.actionDisposables.forEach(d => d.dispose());
        this.actionDisposables = [];

        this.widgetDisposables.forEach(d => d.dispose());
        this.widgetDisposables = [];

        ObservableLike.unsubscribe(this.props.widgets, this.onWidgetsChange);

        delete this.editor;

        window.removeEventListener("resize", this.onResize);
    }

    protected abstract setEditorContent(editor: monaco.editor.IEditor): void;
    protected abstract updateEditorContent(editor: monaco.editor.IEditor): void;
    protected abstract createEditor(domElement: HTMLElement, options?: monaco.editor.IEditorConstructionOptions): monaco.editor.IEditor;

    /**
     * Calls props.onContentUpdated instantly for prop changes and throttled for manual editing to avoid lag.
     */
    protected sendThrottledContentChangedEvent() {
        if (!this.props.onContentUpdated) {
            return;
        }

        if (this.isContentChangeFromProps) {
            this.isContentUserEdited = false;
            // this.editor is always set while component is mounted
            this.props.onContentUpdated(this.editor!);
        } else {
            if (this.throttledContentChangedTimeout) {
                window.clearTimeout(this.throttledContentChangedTimeout);
            }
            const delay = this.isContentUserEdited ? 300 : 0;
            this.throttledContentChangedTimeout = window.setTimeout(() => {
                this.isContentUserEdited = true;
                if (this.editor && this.props.onContentUpdated) {
                    this.props.onContentUpdated(this.editor);
                }
            }, delay);
        }
    }

    protected createEditorModel(content: string, contentType: string | undefined, contentUri: string | undefined) {
        let contentTypeName: string | undefined;
        let uri: monaco.Uri | undefined;

        if (contentType) {
            contentTypeName = contentType;
        } else if (contentUri) {
            if (contentUri.indexOf("://") === -1) {
                contentUri = combineUrlPaths("inmemory://model", contentUri);
            }
            uri = monaco.Uri.parse(contentUri);

            const existingModel = monaco.editor.getModel(uri);
            if (existingModel) {
                if (existingModel.getValue() === content) {
                    // reuse the existing model
                    return existingModel;
                } else {
                    // dispose the previous model and create a new one below.
                    existingModel.dispose();
                }
            }
        }

        return monaco.editor.createModel(content, contentTypeName, uri);
    }

    protected addEditorWidgets(editor: monaco.editor.IEditor, widgetRegistrations: IEditorWidgetRegistration[]): void {
        if (editor) {
            const isDiffEditor = editor.getEditorType() === monaco.editor.EditorType.IDiffEditor;
            const diffEditor = isDiffEditor ? (editor as monaco.editor.IStandaloneDiffEditor) : undefined;
            const codeEditor = isDiffEditor ? diffEditor!.getModifiedEditor() : (editor as monaco.editor.IStandaloneCodeEditor);

            for (const widgetRegistration of widgetRegistrations) {
                const widget = widgetRegistration.addWidget(codeEditor, diffEditor);
                if (widget) {
                    this.widgetDisposables.push({
                        dispose: () => widgetRegistration.removeWidget(widget, codeEditor, diffEditor),
                        id: widgetRegistration.widgetId
                    });
                }

                if (diffEditor) {
                    const diffCodeEditor = diffEditor.getOriginalEditor();
                    const diffWidget = widgetRegistration.addWidget(diffCodeEditor, diffEditor);
                    if (diffWidget) {
                        this.widgetDisposables.push({
                            dispose: () => widgetRegistration.removeWidget(diffWidget, diffCodeEditor, diffEditor),
                            id: widgetRegistration.widgetId
                        });
                    }
                }
            }
        }
    }

    private getEditorOptions(): monaco.editor.IStandaloneEditorConstructionOptions & { theme: string } {
        return {
            scrollBeyondLastLine: !this.props.editorOptions || !this.props.editorOptions.readOnly,
            theme: this.getEditorTheme(),
            mouseWheelZoom: true,
            autoDetectHighContrast: false,
            ...editorDefaultOptions,
            ...this.props.editorOptions,

            // Enable extensions to provide a semantic colorization
            // extension.
            'semanticHighlighting.enabled': true
        };
    }

    private getEditorTheme(): string {
        let monacoTheme = this.props.monacoTheme;
        if (!monacoTheme) {
            const themeService = this.context.pageContext.getService<IVssThemeService>("IVssThemeService");
            monacoTheme = themeService.getCurrentTheme().isDark ? "vs-dark" : "vs";
            if (!this.themeListenerAdded) {
                this.themeListenerAdded = true;
                themeService.subscribe(this.onThemeUpdated);
                this.addUnmountCallback(() => themeService.unsubscribe(this.onThemeUpdated));
            }
        }

        const monacoEnvService = this.context.pageContext.getService<IMonacoEnvironmentService>("IMonacoEnvironmentService");
        return monacoEnvService.defineEditorTheme(monacoTheme);
    }

    private onThemeUpdated = () => {
        if (!this.props.monacoTheme) {
            const themeName = this.getEditorTheme();
            monaco.editor.setTheme(themeName);
        }
    };

    private addEditorActions(actions: monaco.editor.IActionDescriptor[]) {
        if (this.editor) {
            const isDiffEditor = this.editor.getEditorType() === monaco.editor.EditorType.IDiffEditor;
            const diffEditor = isDiffEditor ? (this.editor as monaco.editor.IStandaloneDiffEditor) : undefined;
            const codeEditor = isDiffEditor ? diffEditor!.getModifiedEditor() : (this.editor as monaco.editor.IStandaloneCodeEditor);

            for (const action of actions) {
                this.actionDisposables.push(codeEditor.addAction(action));
                isDiffEditor && this.actionDisposables.push(diffEditor!.getOriginalEditor().addAction(action));
            }
        }
    }

    private onWidgetsChange = (value: {
        addedItems: IEditorWidgetRegistration[];
        index: number;
        removedItems: IEditorWidgetRegistration[];
    }): void => {
        for (const widget of value.removedItems || []) {
            const disposableIndex = this.widgetDisposables.findIndex(d => d.id === widget.widgetId);
            if (disposableIndex >= 0) {
                const deleted = this.widgetDisposables.splice(disposableIndex, 1);
                deleted.forEach(d => d.dispose());
            }
        }

        // this.editor is always defined while the component is mounted
        this.addEditorWidgets(this.editor!, value.addedItems || []);
    };

    private onResize = () => {
        if (this.editor) {
            this.editor.layout();
        }
    };
}
